// Ocean Insight Pro Mobile - Map Screen
// Interactive map with oceanographic data overlays

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { View, StyleSheet, Platform, Dimensions, Share, ScrollView } from 'react-native';
import { Card, Title, Chip, ToggleButton, FAB, useTheme, ActivityIndicator, Menu, Button, Paragraph, IconButton, Dialog, Portal, RadioButton, Modal, List, Divider, Text } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Slider from '@react-native-community/slider';
import { PlatformMap } from '../components/PlatformMap';
import { MaterialIcons } from '@expo/vector-icons';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { SimpleERDDAPOverlay } from '../components/SimpleERDDAPOverlay';
import { clearNavigateFlag, clearMapLocation } from '../store/mapSlice';
import { BoundingBox } from '../types/oceanographic';
import { MAP_CONFIG, DATA_TYPE_CONFIG } from '../constants';
import { locationService, UserLocation, LocationService } from '../services/locationService';
import { logger } from '../utils/logger';
import { waypointService, WaypointCategory } from '../services/waypointService';
import { useRoute } from '@react-navigation/native';
import { fetchPrivateWaypoints, createPrivateWaypoint } from '../store/privateWaypointsSlice';
import { PrivateWaypointsService } from '../services/privateWaypoints';
import WaypointEditModal from '../components/WaypointEditModal';
import { WaypointCategoryModal } from '../components/WaypointCategoryModal';
import { ERDDAPService } from '../services/erddapService';
import { toast } from '../services/toastService';
import { fetchBuoys } from '../store/buoySlice';
import { BuoyData } from '../types/buoy';



// Helper function to get waypoint icon based on category
const getWaypointIcon = (category: string): string => {
  switch (category) {
    case 'fishing_spot': return '🎣';
    case 'reef': return '🪸';
    case 'wreck': return '⚓';
    case 'structure': return '🏗️';
    case 'anchorage': return '⚓';
    case 'hazard': return '⚠️';
    case 'personal': return '📍';
    default: return '📌';
  }
};

export default function MapScreen() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const dispatch = useAppDispatch();
  const route = useRoute();
  const [selectedOverlay, setSelectedOverlay] = useState<string>('');
  const [showCurrentsOverlay, setShowCurrentsOverlay] = useState<boolean>(false);
  const [showRadarOverlay, setShowRadarOverlay] = useState<boolean>(false);
  const [showBathymetryOverlay, setShowBathymetryOverlay] = useState<boolean>(false); // Off by default, user can toggle
  const [bathymetryType, setBathymetryType] = useState<'noaa_bag' | 'multibeam'>('noaa_bag'); // Default to NOAA BAG
  const [bathymetryDialogVisible, setBathymetryDialogVisible] = useState<boolean>(false);
  const [showBuoys, setShowBuoys] = useState<boolean>(false);
  const [showFishingSpots, setShowFishingSpots] = useState(false);
  const [selectedWaypointCategory, setSelectedWaypointCategory] = useState<WaypointCategory | null>(null);
  const [showWaypointCategoryModal, setShowWaypointCategoryModal] = useState(false);
  const [showPrivateWaypoints, setShowPrivateWaypoints] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [newWaypointCoordinates, setNewWaypointCoordinates] = useState<{ latitude: number; longitude: number } | null>(null);
  const [showOceanDataPanel, setShowOceanDataPanel] = useState(false);
  const [chlorophyllDataset, setChlorophyllDataset] = useState<'default' | 'gapFilled' | 's3a' | 's3b'>('default');
  const [showChlorophyllMenu, setShowChlorophyllMenu] = useState(false);
  const [sstDataset, setSstDataset] = useState<'default' | 'leo' | 'gradient'>('default');
  const [showSstMenu, setShowSstMenu] = useState(false);
  const [weedlinesDataset, setWeedlinesDataset] = useState<'1day' | '3day'>('1day');
  const [showWeedlinesMenu, setShowWeedlinesMenu] = useState(false);
  const [currentsDataset, setCurrentsDataset] = useState<'default' | 'miami'>('miami');
  const [showCurrentsMenu, setShowCurrentsMenu] = useState(false);
  const [showBottomControls, setShowBottomControls] = useState(false);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: MAP_CONFIG.defaultRegion.latitude, lng: MAP_CONFIG.defaultRegion.longitude });
  const [zoom, setZoom] = useState(8);
  const [overlayBounds, setOverlayBounds] = useState<BoundingBox | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [measurementMode, setMeasurementMode] = useState(false);
  const [measurementPoints, setMeasurementPoints] = useState<{ latitude: number; longitude: number }[]>([]);
  const [totalDistance, setTotalDistance] = useState<number>(0);
  const [inspectionMode, setInspectionMode] = useState(false);
  const [inspectedCoordinate, setInspectedCoordinate] = useState<{ latitude: number; longitude: number } | null>(null);
  const [inspectedDataValue, setInspectedDataValue] = useState<{ value: number; unit: string; formattedValue: string } | null>(null);
  const [inspectedDepthValue, setInspectedDepthValue] = useState<{ value: number; unit: string; formattedValue: string } | null>(null);
  const [fetchingDataValue, setFetchingDataValue] = useState(false);
  const [fetchingDepthValue, setFetchingDepthValue] = useState(false);
  const [isControlsExpanded, setIsControlsExpanded] = useState(false);
  const [datasetTimestamp, setDatasetTimestamp] = useState<string | null>(null);
  const [reportHotspots, setReportHotspots] = useState<Array<any> | null>(null);
  const [enableClustering, setEnableClustering] = useState(false);
  const [selectedDateOffset, setSelectedDateOffset] = useState<number>(0); // Days offset from current date
  const [availableTimeSteps, setAvailableTimeSteps] = useState<string[]>([]); // Available dates for the dataset
  const [fetchingHistoricalData, setFetchingHistoricalData] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false); // Animation state
  const [animationSpeed, setAnimationSpeed] = useState(2000); // Animation speed in ms (default 2 seconds per frame)
  const [overlayOpacity, setOverlayOpacity] = useState(0.8); // Opacity for dataset overlays
  const [debouncedOpacity, setDebouncedOpacity] = useState(0.8); // Debounced opacity for actual overlay updates
  const [publicWaypoints, setPublicWaypoints] = useState<any[]>([]);
  const [loadingPublicWaypoints, setLoadingPublicWaypoints] = useState(false);
  const mapRef = useRef<any>(null);
  const updateTimer = useRef<NodeJS.Timeout | null>(null);
  const animationTimer = useRef<NodeJS.Timeout | null>(null);
  const opacityTimer = useRef<NodeJS.Timeout | null>(null);

  // ERDDAP datasets publish on their own time axis (for example, LEO ACSPO
  // publishes at 12:00 UTC). Navigation must use these exact timestamps rather
  // than subtracting calendar days from the device clock.
  const selectedTimeStep = availableTimeSteps[Math.abs(selectedDateOffset)];
  const selectedTargetDate = selectedTimeStep ? new Date(selectedTimeStep) : undefined;
  const oldestAvailableOffset = availableTimeSteps.length > 0 ? -(availableTimeSteps.length - 1) : 0;
  
  // Get UI preferences from Redux
  const showZoomButtons = useAppSelector((state) => state.user.uiPreferences.showZoomButtons);
  
  // Get map navigation location from Redux
  const { selectedLocation, navigateToLocation } = useAppSelector((state) => state.map);
  const hasNavigatedRef = useRef(false);
  
  // Get private waypoints from Redux
  const privateWaypoints = useAppSelector((state) => state.privateWaypoints.waypoints);
  const privateWaypointsLoading = useAppSelector((state) => state.privateWaypoints.loading);
  
  // Get buoys from Redux
  const buoys = useAppSelector((state) => state.buoy.buoys);
  const buoysLoading = useAppSelector((state) => state.buoy.loading);
  
  // No longer need ocean data from Redux - using ERDDAP images directly
  const loading = false; // No loading state needed for image overlays
  
  // Navigate to location when requested from another screen
  useEffect(() => {
    if (navigateToLocation && selectedLocation) {
      hasNavigatedRef.current = true;
      setMapCenter({ lat: selectedLocation.latitude, lng: selectedLocation.longitude });
      setZoom(10);
      
      // Clear the navigation flag
      dispatch(clearNavigateFlag());
    }
  }, [navigateToLocation, selectedLocation, dispatch]);

  // State for viewport bounds
  const [viewportBounds, setViewportBounds] = useState<BoundingBox | null>(null);
  const [privateWaypointsInView, setPrivateWaypointsInView] = useState<any[]>([]);

  // Define the function BEFORE using it in useEffect
  const loadPrivateWaypointsInViewport = useCallback(async () => {
    if (!viewportBounds) return;

    try {
      // Load ALL waypoints efficiently (uses cache after first load)
      const allWaypoints = await PrivateWaypointsService.getAllWaypointsOptimized();

      // Filter waypoints that are in the current viewport
      const waypointsInView = allWaypoints.filter(wp =>
        wp.coordinates.latitude >= viewportBounds.minLat &&
        wp.coordinates.latitude <= viewportBounds.maxLat &&
        wp.coordinates.longitude >= viewportBounds.minLon &&
        wp.coordinates.longitude <= viewportBounds.maxLon
      );

      // Limit displayed waypoints based on zoom level for performance
      // Show more waypoints when zoomed in
      const maxWaypoints = zoom >= 10 ? 500 : zoom >= 8 ? 200 : zoom >= 6 ? 100 : 50;

      // If we have too many waypoints, prioritize closer ones to center
      let displayWaypoints = waypointsInView;
      if (waypointsInView.length > maxWaypoints && mapCenter) {
        // Sort by distance from center and take the closest ones
        displayWaypoints = waypointsInView
          .map(wp => ({
            ...wp,
            distance: Math.sqrt(
              Math.pow(wp.coordinates.latitude - mapCenter.lat, 2) +
              Math.pow(wp.coordinates.longitude - mapCenter.lng, 2)
            )
          }))
          .sort((a, b) => a.distance - b.distance)
          .slice(0, maxWaypoints)
          .map(({ distance, ...wp }) => wp);
      } else {
        displayWaypoints = waypointsInView.slice(0, maxWaypoints);
      }

      // Update local state with waypoints in view
      setPrivateWaypointsInView(displayWaypoints);
    } catch (error) {
      console.error('Error loading private waypoints:', error);
    }
  }, [viewportBounds, zoom, mapCenter]);

  // Fetch private waypoints based on viewport when toggled on or viewport changes
  useEffect(() => {
    if (showPrivateWaypoints && viewportBounds) {
      // Debounce to avoid too many requests
      const timer = setTimeout(() => {
        loadPrivateWaypointsInViewport();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showPrivateWaypoints, viewportBounds, zoom, loadPrivateWaypointsInViewport]);

  // Load public waypoints when toggled on or viewport changes
  useEffect(() => {
    if (showFishingSpots && viewportBounds) {
      // Debounce map movement updates
      const timer = setTimeout(() => {
        loadPublicWaypoints();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [showFishingSpots, selectedWaypointCategory, viewportBounds, zoom]);

  const loadPublicWaypoints = async () => {
    if (!viewportBounds || !selectedWaypointCategory) return;
    
    setLoadingPublicWaypoints(true);
    try {
      // Load waypoints in the current viewport with zoom-based limit
      const limit = zoom >= 8 ? 100 : zoom >= 6 ? 50 : 25;
      
      const result = await waypointService.searchWaypoints({
        bounds: {
          north: viewportBounds.maxLat,
          south: viewportBounds.minLat,
          east: viewportBounds.maxLon,
          west: viewportBounds.minLon
        },
        limit,
        category: selectedWaypointCategory
      });
      
      setPublicWaypoints(result.waypoints);
    } catch (error) {
      console.error('Error loading waypoints:', error);
      toast.error('Failed to load waypoints');
    } finally {
      setLoadingPublicWaypoints(false);
    }
  };

  // Animation effect
  useEffect(() => {
    if (isAnimating) {
      // Start animation timer
      animationTimer.current = setInterval(() => {
        setSelectedDateOffset(prevOffset => {
          // Loop animation: go from -7 (7 days ago) to 0 (today)
          if (prevOffset >= 0) {
            return -7; // Reset to 7 days ago
          }
          return prevOffset + 1; // Move forward one day
        });
      }, animationSpeed);
    } else {
      // Stop animation timer
      if (animationTimer.current) {
        clearInterval(animationTimer.current);
        animationTimer.current = null;
      }
    }

    // Cleanup on unmount or when animation stops
    return () => {
      if (animationTimer.current) {
        clearInterval(animationTimer.current);
        animationTimer.current = null;
      }
    };
  }, [isAnimating, animationSpeed]);

  // Load the real ERDDAP time axis whenever the data source changes. Store it
  // newest first so offset 0 is latest and -1 is the immediately prior image.
  useEffect(() => {
    const dataType = selectedOverlay === 'sst' || selectedOverlay === 'chlorophyll' || selectedOverlay === 'ssh' || selectedOverlay === 'weedlines'
      ? selectedOverlay
      : showCurrentsOverlay
        ? 'currents'
        : null;

    if (!dataType) {
      setAvailableTimeSteps([]);
      return;
    }

    const datasetVariant = dataType === 'sst'
      ? (sstDataset === 'default' ? 'default' : 'leo')
      : dataType === 'chlorophyll'
        ? chlorophyllDataset
        : dataType === 'weedlines'
          ? weedlinesDataset
          : currentsDataset;
    let cancelled = false;

    setAvailableTimeSteps([]);
    setSelectedDateOffset(0);
    setFetchingHistoricalData(true);

    ERDDAPService.fetchAvailableTimeSteps(dataType, datasetVariant, 30)
      .then((timeSteps) => {
        if (cancelled) return;

        const newestFirst = [...timeSteps].reverse();
        setAvailableTimeSteps(newestFirst);
      })
      .catch((error) => {
        if (!cancelled) {
          logger.error('Failed to load ERDDAP time steps', error instanceof Error ? error : new Error(String(error)));
          setAvailableTimeSteps([]);
        }
      })
      .finally(() => {
        if (!cancelled) setFetchingHistoricalData(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedOverlay, showCurrentsOverlay, sstDataset, chlorophyllDataset, weedlinesDataset, currentsDataset]);

  // Debounce opacity changes to avoid too many reloads while dragging
  useEffect(() => {
    if (opacityTimer.current) {
      clearTimeout(opacityTimer.current);
    }
    
    opacityTimer.current = setTimeout(() => {
      setDebouncedOpacity(overlayOpacity);
    }, 300); // Wait 300ms after user stops dragging
    
    return () => {
      if (opacityTimer.current) {
        clearTimeout(opacityTimer.current);
      }
    };
  }, [overlayOpacity]);
  
  // Handle navigation params for showing report hotspots
  useEffect(() => {
    const params = route.params as any;
    if (params?.hotspots && params?.enableClustering) {
      setReportHotspots(params.hotspots);
      setEnableClustering(true);
      
      // Turn off waypoint toggles when viewing report hotspots
      setShowFishingSpots(false);
      setShowPrivateWaypoints(false);
      
      // Calculate appropriate zoom level based on hotspot spread
      const lats = params.hotspots.map((h: any) => h.latitude);
      const lngs = params.hotspots.map((h: any) => h.longitude);
      const latSpread = Math.max(...lats) - Math.min(...lats);
      const lngSpread = Math.max(...lngs) - Math.min(...lngs);
      const maxSpread = Math.max(latSpread, lngSpread);
      
      // Set zoom level based on spread
      let calculatedZoom = 7;
      if (maxSpread < 0.5) calculatedZoom = 9;
      else if (maxSpread < 1) calculatedZoom = 8;
      else if (maxSpread < 2) calculatedZoom = 7;
      else if (maxSpread < 4) calculatedZoom = 6;
      else calculatedZoom = 5;
      
      setZoom(calculatedZoom);
    }
  }, [route.params]);

  // Gulf of Mexico fishing spots - loaded asynchronously
  const fishingSpots = publicWaypoints.map((waypoint, index) => ({
    id: String(index + 1),
    latitude: waypoint.latitude,
    longitude: waypoint.longitude,
    title: waypoint.name || `Waypoint ${index + 1}`,
    rating: Math.floor(Math.random() * 3) + 3, // Random rating 3-5
  }));

  const overlayOptions = [
    { id: 'sst', label: 'SST', icon: 'waves' },
    { id: 'chlorophyll', label: 'Chlorophyll', icon: 'flower' },
    { id: 'ssh', label: 'SSH', icon: 'terrain' },
    { id: 'weedlines', label: 'Weedlines', icon: 'waves' },
    { id: 'currents', label: 'Currents', icon: 'weather-windy' },
    { id: 'radar', label: 'Radar', icon: 'weather-pouring' },
    { id: 'buoys', label: 'Buoys', icon: 'lifebuoy' },
  ];

  const chlorophyllDatasets = [
    { id: 'default', label: 'VIIRS (Default)', description: 'NOAA S-NPP VIIRS Chlorophyll-a Daily' },
    { id: 'gapFilled', label: 'VIIRS GAP-Filled', description: 'VIIRS Chlorophyll-a GAP-Filled (DINEOF)' },
    { id: 's3a', label: 'Sentinel-3A OLCI', description: 'Sentinel-3A OLCI Chlorophyll-a Daily' },
    { id: 's3b', label: 'Sentinel-3B OLCI', description: 'Sentinel-3B OLCI Chlorophyll-a Daily' },
  ];

  const sstDatasets = [
    { id: 'default', label: 'Blended (Default)', description: 'NOAA Blended SST Daily' },
    { id: 'leo', label: 'LEO ACSPO', description: 'NOAA LEO ACSPO SST L3S NRT Daily' },
    { id: 'gradient', label: 'SST Gradient', description: 'SST Gradient Magnitude (°C/km)' },
  ];

  const weedlinesDatasets = [
    { id: '1day', label: '1-Day AFAI', description: '1-Day Alternative Floating Algae Index' },
    { id: '3day', label: '3-Day AFAI', description: '3-Day Alternative Floating Algae Index (averaged)' },
  ];

  const currentsDatasets = [
    { id: 'miami', label: 'High-Res (Default)', description: 'High-Resolution Ocean Currents Model' },
    { id: 'default', label: 'Geostrophic', description: 'NOAA Blended Sea Surface Currents' },
  ];

  // Simple function to calculate bounds from region
  const calculateBounds = (region: any): BoundingBox => {
    const halfLatDelta = region.latitudeDelta / 2;
    const halfLonDelta = region.longitudeDelta / 2;
    
    return {
      minLat: Math.max(MAP_CONFIG.globalBounds.minLat, region.latitude - halfLatDelta),
      maxLat: Math.min(MAP_CONFIG.globalBounds.maxLat, region.latitude + halfLatDelta),
      minLon: Math.max(MAP_CONFIG.globalBounds.minLon, region.longitude - halfLonDelta),
      maxLon: Math.min(MAP_CONFIG.globalBounds.maxLon, region.longitude + halfLonDelta)
    };
  };

  // Prepare markers for Leaflet
  // Format coordinates to DMS (Degrees Minutes Seconds)
  const formatCoordinateDMS = (lat: number, lon: number): string => {
    const formatDMS = (deg: number, isLat: boolean): string => {
      const absolute = Math.abs(deg);
      const d = Math.floor(absolute);
      const m = Math.floor((absolute - d) * 60);
      const s = ((absolute - d - m / 60) * 3600).toFixed(3);
      const dir = isLat ? (deg >= 0 ? 'N' : 'S') : (deg >= 0 ? 'E' : 'W');
      return `${d}°${m}'${s}"${dir}`;
    };
    
    return `${formatDMS(lat, true)} ${formatDMS(lon, false)}`;
  };

  // Format distance for display in statute miles
  const formatDistance = (meters: number): string => {
    const miles = meters * 0.000621371; // Convert meters to statute miles
    if (miles < 0.1) {
      return `${(miles * 5280).toFixed(0)} ft`; // Show feet for very short distances
    } else if (miles < 10) {
      return `${miles.toFixed(2)} mi`;
    } else {
      return `${miles.toFixed(1)} mi`;
    }
  };

  const mapMarkers = useMemo(() => {
    const markers = [];
    
    // Report hotspots (no longer exclusive - can show with other markers)
    if (reportHotspots && reportHotspots.length > 0) {
      reportHotspots.forEach((hotspot, index) => {
        markers.push({
          position: { lat: hotspot.latitude, lng: hotspot.longitude },
          icon: '🎯',
          size: [36, 36] as [number, number],  // Slightly larger for better visibility
          title: hotspot.title || `Hotspot #${index + 1}`,
          type: 'hotspot',  // Add type to distinguish
          hotspotNumber: index + 1,  // Add ranking number
          temperature: hotspot.temperature || hotspot.waterConditions?.temperature,
          chlorophyll: hotspot.chlorophyll || hotspot.waterConditions?.chlorophyll,
          currentSpeed: hotspot.currentSpeed || hotspot.waterConditions?.currentSpeed,
          currentDirection: hotspot.currentDirection || hotspot.waterConditions?.currentDirection,
          ssh: hotspot.ssh || hotspot.waterConditions?.ssh,
          confidence: hotspot.confidence,
          score: hotspot.score,
          reasons: hotspot.reasons,
          gradients: hotspot.gradients,
          nearbyStructures: hotspot.nearbyStructures
        });
      });
    }
    
    // User location marker
    if (userLocation) {
      markers.push({
        position: { lat: userLocation.latitude, lng: userLocation.longitude },
        icon: '📍',
        size: [32, 32] as [number, number],
        title: 'Your Location'
      });
    }
    
    // Selected location marker - only show if there are no report hotspots
    if (selectedLocation && !navigateToLocation && (!reportHotspots || reportHotspots.length === 0)) {
      markers.push({
        position: { lat: selectedLocation.latitude, lng: selectedLocation.longitude },
        icon: '🎯',
        size: [32, 32] as [number, number],
        title: 'Selected Fishing Hotspot'
      });
    }
    
    // Fishing spots/waypoints - with distinct visual style
    if (showFishingSpots) {
      fishingSpots.forEach(spot => {
        markers.push({
          position: { lat: spot.latitude, lng: spot.longitude },
          icon: '🐟',  // Fish icon for waypoints
          size: [24, 24] as [number, number],  // Smaller size
          title: spot.title,
          type: 'waypoint',  // Add type to distinguish
          noCluster: true    // Prevent clustering for waypoints
        });
      });
    }

    // Private waypoints - with standard pin marker
    // Only show waypoints when zoomed in enough (zoom >= 6)
    if (showPrivateWaypoints && privateWaypointsInView.length > 0 && zoom >= 6) {
      privateWaypointsInView.forEach(waypoint => {
        // Check if waypoint has isVisible property (backward compatibility)
        const isVisible = waypoint.isVisible !== undefined ? waypoint.isVisible : true;
        if (isVisible) {
          markers.push({
            position: { lat: waypoint.coordinates.latitude, lng: waypoint.coordinates.longitude },
            icon: '📍',  // Standard red pin emoji
            size: zoom >= 8 ? [28, 28] as [number, number] : [24, 24] as [number, number],  // Larger when zoomed in
            title: waypoint.name,
            description: waypoint.description,
            type: 'private-waypoint',
            category: waypoint.category,
            noCluster: true // Never cluster personal waypoints
          });
        }
      });
    }
    
    // Add buoy markers
    if (showBuoys && buoys.length > 0) {
      buoys.forEach(buoy => {
        markers.push({
          position: { lat: buoy.latitude, lng: buoy.longitude },
          icon: '🛟',  // Life buoy emoji for NOAA buoys
          size: [32, 32] as [number, number],
          title: buoy.station_name || buoy.station_id,
          type: 'buoy',
          buoyData: buoy,  // Include full buoy data for popup
          noCluster: true   // Don't cluster buoys
        });
      });
    }
    
    // Don't add any markers for measurement points - let the line do the work
    
    return markers;
  }, [userLocation, selectedLocation, navigateToLocation, showFishingSpots, fishingSpots, reportHotspots, showPrivateWaypoints, privateWaypointsInView, measurementMode, measurementPoints, totalDistance, zoom, showBuoys, buoys]);

  // Set map ready when component mounts
  useEffect(() => {
    setIsMapReady(true);
  }, []);
  

  // Get user location on mount and center on it
  useEffect(() => {
    const initLocation = async () => {
      try {
        // Check if location services are enabled
        const servicesEnabled = await LocationService.isLocationServicesEnabled();
        if (!servicesEnabled) {
          setLocationError('Location services disabled');
          // Don't return - continue with default location
        }

        // Get current location and center on it
        const location = await locationService.getCurrentLocation();
        if (location) {
          setUserLocation(location);
          // Only center the map on user's location if we haven't navigated from another screen
          if (!hasNavigatedRef.current) {
            setMapCenter({ lat: location.latitude, lng: location.longitude });
            setZoom(10); // Set a reasonable zoom level for user location
          }
          
          // Don't fetch ocean data automatically - wait for user to select a layer
        } else {
          setLocationError('Unable to get location');
          // Don't fetch ocean data automatically
        }
      } catch (error) {
        logger.error('Location initialization error', error instanceof Error ? error : new Error(String(error)));
        setLocationError('Location error');
      }
    };

    if (isMapReady) {
      initLocation();
    }
  }, [isMapReady]);

  // Watch user location
  useEffect(() => {
    let cleanup = false;
    
    const startWatching = async () => {
      await locationService.watchLocation((location) => {
        if (!cleanup) {
          setUserLocation(location);
          logger.debug('Location updated', { 
            lat: location.latitude.toFixed(6), 
            lon: location.longitude.toFixed(6) 
          });
        }
      });
    };

    if (isMapReady) {
      startWatching();
    }

    return () => {
      cleanup = true;
      locationService.stopWatchingLocation();
    };
  }, [isMapReady]);

  // Get spot color based on rating
  const getSpotColor = (rating: number): string => {
    return '#2196F3'; // Single blue color for all waypoints
  };

  // Fetch buoys when toggled on or viewport changes
  useEffect(() => {
    if (showBuoys && viewportBounds) {
      const timer = setTimeout(() => {
        dispatch(fetchBuoys({
          maxLat: viewportBounds.maxLat,
          minLat: viewportBounds.minLat,
          maxLon: viewportBounds.maxLon,
          minLon: viewportBounds.minLon
        }));
      }, 500); // Debounce for 500ms
      return () => clearTimeout(timer);
    }
  }, [showBuoys, viewportBounds, dispatch]);

  // Initialize bounds based on current map view
  useEffect(() => {
    if (selectedOverlay || showCurrentsOverlay || showRadarOverlay || showBathymetryOverlay) {
      // Calculate bounds from center and zoom
      // Get screen dimensions to calculate proper bounds
      const { height, width } = Dimensions.get('window');
      const aspectRatio = width / height;
      
      // Calculate the span of latitude/longitude visible on screen
      // These formulas give us the approximate visible area based on zoom level
      const latSpan = 180 / Math.pow(2, zoom - 1); // Adjusted for better coverage
      const lonSpan = latSpan * aspectRatio * 1.5; // Account for aspect ratio and add buffer
      
      const bounds = {
        minLat: Math.max(MAP_CONFIG.globalBounds.minLat, mapCenter.lat - latSpan),
        maxLat: Math.min(MAP_CONFIG.globalBounds.maxLat, mapCenter.lat + latSpan),
        minLon: Math.max(MAP_CONFIG.globalBounds.minLon, mapCenter.lng - lonSpan),
        maxLon: Math.min(MAP_CONFIG.globalBounds.maxLon, mapCenter.lng + lonSpan)
      };
      
      logger.info('Setting initial bounds for overlay', { selectedOverlay, showCurrentsOverlay, showRadarOverlay, showBathymetryOverlay, bounds, zoom, aspectRatio });
      setOverlayBounds(bounds);
    } else {
      setOverlayBounds(null);
    }
  }, [selectedOverlay, showCurrentsOverlay, showRadarOverlay, showBathymetryOverlay]);
  
  // Handle map moves with debounce
  const handleMapMove = useCallback((newCenter: { lat: number; lng: number }, newZoom: number, newBounds: BoundingBox) => {
    setMapCenter(newCenter);
    setZoom(newZoom);
    
    // Update viewport bounds for waypoint loading
    setViewportBounds({
      minLat: newBounds.minLat,
      maxLat: newBounds.maxLat,
      minLon: newBounds.minLon,
      maxLon: newBounds.maxLon
    });
    
    // Clear any existing timer
    if (updateTimer.current) {
      clearTimeout(updateTimer.current);
    }
    
    // Update overlay bounds after user stops moving (debounced)
    if (selectedOverlay || showCurrentsOverlay || showRadarOverlay || showBathymetryOverlay) {
      updateTimer.current = setTimeout(() => {
        logger.info('Updating overlay bounds after map movement', { 
          newBounds, 
          zoom: newZoom,
          selectedOverlay,
          showCurrentsOverlay,
          showRadarOverlay,
          showBathymetryOverlay 
        });
        // Force a new bounds object to ensure React detects the change
        setOverlayBounds({
          minLat: newBounds.minLat,
          maxLat: newBounds.maxLat,
          minLon: newBounds.minLon,
          maxLon: newBounds.maxLon
        });
      }, 500); // Wait 500ms after movement stops
    }
  }, [selectedOverlay, showCurrentsOverlay, showRadarOverlay, showBathymetryOverlay]);

  // Measurement polylines and bounding box for Leaflet
  const mapShapes = useMemo(() => {
    const shapes = [];
    
    if (measurementMode && measurementPoints.length >= 2) {
      // Create polyline from all points
      shapes.push({
        shapeType: 'polyline' as const,
        positions: measurementPoints.map(p => ({ lat: p.latitude, lng: p.longitude })),
        color: '#FF0000',  // Red line for visibility
        weight: 3,
        distance: totalDistance
      });
    }
    
    
    return shapes;
  }, [measurementMode, measurementPoints, totalDistance]);
  
  // Generate overlay URLs for Leaflet
  const [overlayConfigs, setOverlayConfigs] = useState<Array<{
    url: string;
    bounds: BoundingBox;
    zIndex?: number;
    type?: 'tile' | 'image' | 'custom-bag';
    opacity?: number;
  }>>([]);
  
  // Separate state for bathymetry to prevent recreation
  const [bathymetryConfig, setBathymetryConfig] = useState<{
    url: string;
    bounds: BoundingBox;
    zIndex?: number;
    type?: 'tile' | 'image' | 'custom-bag' | 'custom-multibeam';
    opacity?: number;
  } | null>(null);
  
  // Separate effect for bathymetry that depends on showBathymetryOverlay and bathymetryType
  useEffect(() => {
    if (showBathymetryOverlay) {
      setBathymetryConfig({
        url: bathymetryType === 'multibeam' ? 'MULTIBEAM_BATHYMETRY' : 'NOAA_BAG_BATHYMETRY',
        bounds: { minLat: -90, maxLat: 90, minLon: -180, maxLon: 180 },
        zIndex: 1500,
        type: bathymetryType === 'multibeam' ? 'custom-multibeam' as const : 'custom-bag' as const,
        opacity: 0.7
      });
    } else {
      setBathymetryConfig(null);
    }
  }, [showBathymetryOverlay, bathymetryType]);
  
  useEffect(() => {
    const generateOverlayUrls = async () => {
      // For bathymetry-only, we don't need bounds since it's a tile layer that handles its own bounds
      const needsBounds = selectedOverlay || showCurrentsOverlay || showRadarOverlay;
      
      if (needsBounds && overlayBounds) {
        try {
          const { ERDDAPService } = await import('../services/erddapService');
          const { WeatherRadarService } = await import('../services/weatherRadarService');
          const overlays: Array<{ url: string; bounds: BoundingBox; zIndex?: number; type?: 'tile' | 'image' | 'custom-bag'; opacity?: number }> = [];
          
          // Add base data overlay (above bathymetry)
          if (selectedOverlay && selectedOverlay !== 'currents' && selectedOverlay !== 'radar' && selectedOverlay !== 'bathymetry') {
            let url: string;
            const bounds = overlayBounds;
            
            {
              // Use dynamic SST overlay for better temperature visualization
              if (selectedOverlay === 'sst') {
                const targetDate = selectedTargetDate;

                // CoastWatch permits browser image loads but not cross-origin
                // JSON reads. Avoid dynamic-range data fetches on web and use
                // the same exact-date image endpoint as the native fallback.
                if (Platform.OS === 'web') {
                  url = ERDDAPService.getImageOverlayURL('sst', overlayBounds, { sstDataset, targetDate });
                } else {
                  try {
                    url = await ERDDAPService.getDynamicSSTOverlayURL(overlayBounds, sstDataset, targetDate);
                    if (!url || url.length === 0) {
                      throw new Error('Generated SST URL is empty');
                    }
                  } catch (error) {
                    logger.warn('Failed to get dynamic SST overlay, falling back to default', { error: error instanceof Error ? error.message : String(error) });
                    url = ERDDAPService.getImageOverlayURL('sst', overlayBounds, { sstDataset, targetDate });
                  }
                }
              } else {
                const targetDate = selectedTargetDate;
                
                url = ERDDAPService.getImageOverlayURL(
                  selectedOverlay as 'chlorophyll' | 'ssh' | 'weedlines',
                  overlayBounds,
                  selectedOverlay === 'chlorophyll' ? { chlorophyllDataset, targetDate } : 
                  selectedOverlay === 'weedlines' ? { weedlinesDataset, targetDate } : 
                  { targetDate }
                );
              }
            }
            
            overlays.push({
              url,
              bounds,
              zIndex: 2, // Always above bathymetry
              opacity: debouncedOpacity
            });
            
            // Fetch or calculate timestamp for the dataset
            {
              if (selectedTimeStep) {
                setDatasetTimestamp(selectedTimeStep);
              } else {
                // For offset 0, fetch the latest timestamp
                let datasetVariant = 'default';
                if (selectedOverlay === 'sst') {
                  datasetVariant = sstDataset;
                } else if (selectedOverlay === 'chlorophyll') {
                  datasetVariant = chlorophyllDataset;
                } else if (selectedOverlay === 'weedlines') {
                  datasetVariant = weedlinesDataset as any;
                }
                
                const timestamp = await ERDDAPService.fetchDatasetTimestamp(
                  selectedOverlay as 'sst' | 'chlorophyll' | 'ssh' | 'currents' | 'weedlines',
                  datasetVariant as 'default' | 'leo' | 'gapFilled' | 's3a' | 's3b'
                );
                setDatasetTimestamp(timestamp);
              }
            }
          }
          
          // Add currents overlay on top (higher z-index)
          if (showCurrentsOverlay) {
            const targetDate = selectedTargetDate;
            if (selectedTimeStep) {
              setDatasetTimestamp(selectedTimeStep);
            } else {
              // Fetch timestamp for currents if at current date
              const timestamp = await ERDDAPService.fetchDatasetTimestamp(
                'currents',
                currentsDataset as 'default' | 'leo' | 'gapFilled' | 's3a' | 's3b'
              );
              setDatasetTimestamp(timestamp);
            }
            
            const url = ERDDAPService.getImageOverlayURL('currents', overlayBounds, { 
              currentsDataset,
              targetDate 
            });
            overlays.push({
              url,
              bounds: overlayBounds,
              zIndex: 3, // Above other overlays
              opacity: debouncedOpacity
            });
          }
          
          // Add weather radar overlay on top (highest z-index)
          if (showRadarOverlay) {
            const radarUrl = await WeatherRadarService.getLatestRadarOverlayURL(overlayBounds);
            if (radarUrl) {
              overlays.push({
                url: radarUrl,
                bounds: overlayBounds,
                zIndex: 4, // Highest z-index
                type: 'tile' as const, // Specify this is a tile layer, not an image overlay
                opacity: 0.7 // Fixed opacity for radar overlay
              });
            }
          }
          
          logger.info('Generated overlay URLs for Leaflet', { 
            overlayCount: overlays.length,
            types: [selectedOverlay, showCurrentsOverlay ? 'currents' : null, showRadarOverlay ? 'radar' : null].filter(Boolean)
          });
          // Validate overlays before setting
          if (overlays.length > 0) {
            // Filter out invalid overlays but keep valid ones
            const validOverlays = overlays.filter(o => o.url && o.url.length > 0);
            if (validOverlays.length > 0) {
              setOverlayConfigs(validOverlays);
              logger.info('Successfully set overlay configurations', { 
                count: validOverlays.length,
                firstUrl: validOverlays[0]?.url?.substring(0, 200),
                allUrlLengths: validOverlays.map(o => o.url.length)
              });
            } else {
              logger.warn('No valid overlay URLs generated', { overlays });
              toast.error('Failed to generate valid overlay URLs');
              setOverlayConfigs([]);
            }
          } else {
            setOverlayConfigs([]);
          }
          // Reset loading state after successful fetch
          setFetchingHistoricalData(false);
        } catch (error) {
          logger.error('Error generating overlay URLs', error instanceof Error ? error : new Error(String(error)));
          
          // Check if this is a 503 error that's being retried
          const is503Error = error instanceof Error && 
                            (error.message.includes('503') || error.message.includes('Service Unavailable'));
          
          if (is503Error) {
            // Don't clear overlays or show error for 503s - they're being retried
            logger.info('503 error - service is retrying automatically');
            toast.info('NOAA servers are busy. Retrying...');
          } else {
            setOverlayConfigs([]);
            setDatasetTimestamp(null);
            setFetchingHistoricalData(false);
            // Show user-friendly error message for non-503 errors
            toast.error('Failed to load ocean data. Please try a different dataset.');
          }
        }
      } else {
        setOverlayConfigs([]);
        setDatasetTimestamp(null);
      }
    };
    
    generateOverlayUrls();
  }, [selectedOverlay, showCurrentsOverlay, showRadarOverlay, overlayBounds, chlorophyllDataset, sstDataset, weedlinesDataset, currentsDataset, zoom, selectedDateOffset, selectedTimeStep, debouncedOpacity]);

  
  // Format dataset timestamp for display
  const formatDatasetTimestamp = (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffHours = diffMs / (1000 * 60 * 60);
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      
      // Format the date
      const dateStr = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
      
      // Add time for recent data
      if (diffHours < 24) {
        const timeStr = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        });
        return `${dateStr} ${timeStr}`;
      } else if (diffDays < 7) {
        return `${dateStr} (${Math.floor(diffDays)} days ago)`;
      } else {
        return dateStr;
      }
    } catch (error) {
      return timestamp; // Return raw timestamp if parsing fails
    }
  };

  // Clear measurement
  const clearMeasurement = () => {
    setMeasurementPoints([]);
    setTotalDistance(0);
  };

  // Zoom in function
  const zoomIn = () => {
    const newZoom = Math.min(zoom + 1, 18);
    setZoom(newZoom);
    
    // Update overlay bounds when zooming
    if (selectedOverlay || showCurrentsOverlay || showRadarOverlay || showBathymetryOverlay) {
      const { height, width } = Dimensions.get('window');
      const aspectRatio = width / height;
      
      const latSpan = 180 / Math.pow(2, newZoom - 1);
      const lonSpan = latSpan * aspectRatio * 1.5;
      
      const newBounds = {
        minLat: Math.max(MAP_CONFIG.globalBounds.minLat, mapCenter.lat - latSpan),
        maxLat: Math.min(MAP_CONFIG.globalBounds.maxLat, mapCenter.lat + latSpan),
        minLon: Math.max(MAP_CONFIG.globalBounds.minLon, mapCenter.lng - lonSpan),
        maxLon: Math.min(MAP_CONFIG.globalBounds.maxLon, mapCenter.lng + lonSpan)
      };
      
      logger.info('Updating overlay bounds on zoom in', { 
        newZoom,
        newBounds,
        selectedOverlay,
        showCurrentsOverlay,
        showRadarOverlay,
        showBathymetryOverlay,
        aspectRatio
      });
      setOverlayBounds(newBounds);
    }
  };

  // Zoom out function
  const zoomOut = () => {
    const newZoom = Math.max(zoom - 1, 3);
    setZoom(newZoom);
    
    // Update overlay bounds when zooming
    if (selectedOverlay || showCurrentsOverlay || showRadarOverlay || showBathymetryOverlay) {
      const { height, width } = Dimensions.get('window');
      const aspectRatio = width / height;
      
      const latSpan = 180 / Math.pow(2, newZoom - 1);
      const lonSpan = latSpan * aspectRatio * 1.5;
      
      const newBounds = {
        minLat: Math.max(MAP_CONFIG.globalBounds.minLat, mapCenter.lat - latSpan),
        maxLat: Math.min(MAP_CONFIG.globalBounds.maxLat, mapCenter.lat + latSpan),
        minLon: Math.max(MAP_CONFIG.globalBounds.minLon, mapCenter.lng - lonSpan),
        maxLon: Math.min(MAP_CONFIG.globalBounds.maxLon, mapCenter.lng + lonSpan)
      };
      
      logger.info('Updating overlay bounds on zoom out', { 
        newZoom,
        newBounds,
        selectedOverlay,
        showCurrentsOverlay,
        showRadarOverlay,
        showBathymetryOverlay,
        aspectRatio
      });
      setOverlayBounds(newBounds);
    }
  };

  // Center on user location
  const centerOnUserLocation = async () => {
    try {
      const location = await locationService.getCurrentLocation();
      if (location) {
        setMapCenter({ lat: location.latitude, lng: location.longitude });
        setZoom(12);
      }
    } catch (error) {
      logger.error('Error centering on user location', error instanceof Error ? error : new Error(String(error)));
    }
  };

  return (
    <View style={styles.container}>
      {/* Map View */}
      <PlatformMap
        center={mapCenter}
        zoom={zoom}
        markers={mapMarkers}
        overlays={[...(bathymetryConfig ? [bathymetryConfig] : []), ...overlayConfigs]}
        shapes={mapShapes}
        enableClustering={enableClustering}
        baseMapType="ocean"
        onMapLongPress={(lat, lng) => {
          // Show create waypoint modal
          setIsCreateModalVisible(true);
          setNewWaypointCoordinates({ latitude: lat, longitude: lng });
        }}
        onMarkerClick={(position) => {
          // Handle marker clicks for measurement mode
          if (measurementMode) {
            const coordinate = { latitude: position.lat, longitude: position.lng };
            
            // For two-point measurement, replace the second point if we already have 2
            let newPoints;
            if (measurementPoints.length >= 2) {
              // Reset to just the new point
              newPoints = [coordinate];
              setTotalDistance(0);
            } else {
              newPoints = [...measurementPoints, coordinate];
              
              // Calculate distance when we have exactly 2 points
              if (newPoints.length === 2) {
                const distance = LocationService.calculateDistance(
                  newPoints[0].latitude,
                  newPoints[0].longitude,
                  newPoints[1].latitude,
                  newPoints[1].longitude
                );
                setTotalDistance(distance);
              }
            }
            
            setMeasurementPoints(newPoints);
          }
        }}
        onMapClick={async (lat, lng) => {
          if (inspectionMode) {
            // In inspection mode, capture the center coordinate instead of click location
            const centerCoordinate = { latitude: mapCenter.lat, longitude: mapCenter.lng };
            setInspectedCoordinate(centerCoordinate);
            
            // Always fetch depth data
            setFetchingDepthValue(true);
            setInspectedDepthValue(null);
            
            // Fetch depth data
            ERDDAPService.getDepthAtPoint(centerCoordinate)
              .then(depthValue => {
                if (depthValue) {
                  setInspectedDepthValue(depthValue);
                }
              })
              .catch(error => {
                logger.error('Failed to fetch depth data at point', error instanceof Error ? error : new Error(String(error)));
              })
              .finally(() => {
                setFetchingDepthValue(false);
              });
            
            // Fetch ocean data value if a layer is selected
            if (selectedOverlay || showCurrentsOverlay) {
              setFetchingDataValue(true);
              setInspectedDataValue(null);
              
              try {
                // Determine which data type to fetch
                let dataType: 'sst' | 'chlorophyll' | 'ssh' | 'currents' | null = null;
                let datasetVariant: 'default' | 'leo' | 'gapFilled' | 's3a' | 's3b' | undefined;
                
                if (showCurrentsOverlay) {
                  dataType = 'currents';
                } else if (selectedOverlay) {
                  dataType = selectedOverlay as 'sst' | 'chlorophyll' | 'ssh';
                  
                  // Set dataset variant (excluding gradient which is not supported for point data)
                  if (dataType === 'sst' && sstDataset !== 'gradient') {
                    datasetVariant = sstDataset as 'default' | 'leo';
                  } else if (dataType === 'chlorophyll') {
                    datasetVariant = chlorophyllDataset;
                  }
                }
                
                if (dataType) {
                  const dataValue = await ERDDAPService.getDataAtPoint(
                    dataType,
                    centerCoordinate,
                    datasetVariant,
                    selectedTargetDate
                  );
                  
                  if (dataValue) {
                    setInspectedDataValue(dataValue);
                  }
                }
              } catch (error) {
                logger.error('Failed to fetch ocean data at point', error instanceof Error ? error : new Error(String(error)));
              } finally {
                setFetchingDataValue(false);
              }
            }
          } else if (measurementMode) {
            const coordinate = { latitude: lat, longitude: lng };
            
            // For two-point measurement, replace the second point if we already have 2
            let newPoints;
            if (measurementPoints.length >= 2) {
              // Reset to just the new point
              newPoints = [coordinate];
              setTotalDistance(0);
            } else {
              newPoints = [...measurementPoints, coordinate];
              
              // Calculate distance when we have exactly 2 points
              if (newPoints.length === 2) {
                const distance = LocationService.calculateDistance(
                  newPoints[0].latitude,
                  newPoints[0].longitude,
                  newPoints[1].latitude,
                  newPoints[1].longitude
                );
                setTotalDistance(distance);
              }
            }
            
            setMeasurementPoints(newPoints);
          }
        }}
        onMapMove={handleMapMove}
      />
      
      {/* Distance display for measurement - now shown on the map line itself */}

      {/* Top Left Menu Toggle */}
      <FAB
        style={[
          styles.menuToggleFab,
          { 
            backgroundColor: theme.colors.surface,
            top: 10,
            left: 10,
          }
        ]}
        icon={showOceanDataPanel ? "chevron-up" : "layers"}
        small
        onPress={() => setShowOceanDataPanel(!showOceanDataPanel)}
        color={theme.colors.primary}
      />
      


      {/* Overlay Controls - Collapsible */}
      {showOceanDataPanel && (
        <Card style={[styles.overlayCard, { 
          backgroundColor: theme.colors.surface, 
          top: 60, 
          marginHorizontal: insets.left || insets.right ? Math.max(insets.left, insets.right) : 16,
          left: insets.left + 16,
          right: undefined,
          bottom: undefined
        }]}>
        <Card.Content style={styles.compactCardContent}>
          <View style={styles.overlayButtons}>
            {overlayOptions.map((option) => (
              <Chip
                key={option.id}
                selected={
                  option.id === 'currents' ? showCurrentsOverlay : 
                  option.id === 'radar' ? showRadarOverlay :
                  option.id === 'buoys' ? showBuoys :
                  selectedOverlay === option.id
                }
                onPress={() => {
                  if (option.id === 'currents') {
                    // Currents is a toggle overlay that can be shown with other data
                    setShowCurrentsOverlay(!showCurrentsOverlay);
                  } else if (option.id === 'radar') {
                    // Radar is also a toggle overlay that can be shown with other data
                    setShowRadarOverlay(!showRadarOverlay);
                  } else if (option.id === 'buoys') {
                    // Buoys is a toggle that shows NOAA buoy markers
                    setShowBuoys(!showBuoys);
                  } else {
                    // Other overlays are mutually exclusive
                    if (selectedOverlay === option.id) {
                      setSelectedOverlay('');
                    } else {
                      setSelectedOverlay(option.id);
                      // Stop animation when switching overlays
                      setIsAnimating(false);
                    }
                  }
                }}
                style={[
                  styles.overlayChip,
                  ((option.id === 'currents' && showCurrentsOverlay) || 
                   (option.id === 'radar' && showRadarOverlay) ||
                   (option.id === 'buoys' && showBuoys) ||
                   (option.id !== 'currents' && option.id !== 'radar' && option.id !== 'buoys' && selectedOverlay === option.id)) && { 
                    backgroundColor: theme.colors.primary 
                  }
                ]}
                textStyle={{
                  color: ((option.id === 'currents' && showCurrentsOverlay) || 
                         (option.id === 'radar' && showRadarOverlay) ||
                         (option.id === 'buoys' && showBuoys) ||
                         (option.id !== 'currents' && option.id !== 'radar' && option.id !== 'buoys' && selectedOverlay === option.id)) 
                    ? 'white' : theme.colors.onSurface
                }}
                icon={option.icon}
                accessibilityLabel={`Select ${option.label} overlay`}
                accessibilityRole="button"
                accessibilityState={{ selected: selectedOverlay === option.id }}
              >
                {option.label}
              </Chip>
            ))}
          </View>
          {selectedOverlay === 'chlorophyll' && (
            <View style={styles.chlorophyllDatasetContainer}>
              <Title style={styles.datasetLabel}>Dataset:</Title>
              <Menu
                visible={showChlorophyllMenu}
                onDismiss={() => setShowChlorophyllMenu(false)}
                anchor={
                  <Button
                    mode="outlined"
                    onPress={() => setShowChlorophyllMenu(true)}
                    icon="chevron-down"
                    contentStyle={styles.datasetButton}
                    style={styles.datasetButtonContainer}
                    compact
                  >
                    {chlorophyllDatasets.find(d => d.id === chlorophyllDataset)?.label || 'Select Dataset'}
                  </Button>
                }
              >
                {chlorophyllDatasets.map((dataset) => (
                  <Menu.Item
                    key={dataset.id}
                    onPress={() => {
                      setChlorophyllDataset(dataset.id as 'default' | 'gapFilled' | 's3a' | 's3b');
                      setShowChlorophyllMenu(false);
                      // Reset history when switching datasets.
                      setSelectedDateOffset(0);
                      setIsAnimating(false);
                    }}
                    title={dataset.label}
                    leadingIcon={chlorophyllDataset === dataset.id ? 'check' : undefined}
                  />
                ))}
              </Menu>
              {/* Date Navigation Controls for Chlorophyll */}
              {(
                <View style={styles.dateNavigationContainer}>
                  <Button
                    mode="text"
                    icon={isAnimating ? "pause" : "play"}
                    onPress={() => {
                      setIsAnimating(!isAnimating);
                      if (!isAnimating) {
                        setSelectedDateOffset(-Math.min(7, availableTimeSteps.length - 1));
                      }
                    }}
                    disabled={fetchingHistoricalData || availableTimeSteps.length < 2}
                    compact
                    contentStyle={styles.dateNavButtonContent}
                    labelStyle={styles.dateNavButtonLabel}
                  >
                    {' '}
                  </Button>
                  <Button
                    mode="text"
                    icon="chevron-left"
                    onPress={() => {
                      setIsAnimating(false);
                      setSelectedDateOffset(selectedDateOffset - 1);
                      setFetchingHistoricalData(true);
                    }}
                    disabled={fetchingHistoricalData || selectedDateOffset <= oldestAvailableOffset || isAnimating}
                    compact
                    contentStyle={styles.dateNavButtonContent}
                    labelStyle={styles.dateNavButtonLabel}
                  >
                    {' '}
                  </Button>
                  <View style={styles.dateDisplay}>
                    <Paragraph style={styles.dateText}>
                      {selectedTimeStep
                        ? new Date(selectedTimeStep).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                        : 'Loading…'}
                    </Paragraph>
                    {isAnimating && (
                      <Paragraph style={styles.animationIndicator}>
                        Day {Math.abs(selectedDateOffset) + 1} of 8
                      </Paragraph>
                    )}
                  </View>
                  <Button
                    mode="text"
                    icon="chevron-right"
                    onPress={() => {
                      setIsAnimating(false);
                      setSelectedDateOffset(selectedDateOffset + 1);
                      setFetchingHistoricalData(true);
                    }}
                    disabled={fetchingHistoricalData || selectedDateOffset >= 0 || isAnimating}
                    compact
                    contentStyle={styles.dateNavButtonContent}
                    labelStyle={styles.dateNavButtonLabel}
                  >
                    {' '}
                  </Button>
                </View>
              )}
            </View>
          )}
          {selectedOverlay === 'sst' && (
            <View style={styles.chlorophyllDatasetContainer}>
              <Title style={styles.datasetLabel}>Dataset:</Title>
              <Menu
                visible={showSstMenu}
                onDismiss={() => setShowSstMenu(false)}
                anchor={
                  <Button
                    mode="outlined"
                    onPress={() => setShowSstMenu(true)}
                    icon="chevron-down"
                    contentStyle={styles.datasetButton}
                    style={styles.datasetButtonContainer}
                    compact
                  >
                    {sstDatasets.find(d => d.id === sstDataset)?.label || 'Select Dataset'}
                  </Button>
                }
              >
                {sstDatasets.map((dataset) => (
                  <Menu.Item
                    key={dataset.id}
                    onPress={() => {
                      setSstDataset(dataset.id as 'default' | 'leo' | 'gradient');
                      setShowSstMenu(false);
                      // Reset history when switching datasets.
                      setSelectedDateOffset(0);
                      setIsAnimating(false);
                    }}
                    title={dataset.label}
                    leadingIcon={sstDataset === dataset.id ? 'check' : undefined}
                  />
                ))}
              </Menu>
              {/* Date Navigation Controls for SST */}
              {(
                <View style={styles.dateNavigationContainer}>
                  <Button
                    mode="text"
                    icon={isAnimating ? "pause" : "play"}
                    onPress={() => {
                      setIsAnimating(!isAnimating);
                      if (!isAnimating) {
                        setSelectedDateOffset(-Math.min(7, availableTimeSteps.length - 1));
                      }
                    }}
                    disabled={fetchingHistoricalData || availableTimeSteps.length < 2}
                    compact
                    contentStyle={styles.dateNavButtonContent}
                    labelStyle={styles.dateNavButtonLabel}
                  >
                    {' '}
                  </Button>
                  <Button
                    mode="text"
                    icon="chevron-left"
                    onPress={() => {
                      setIsAnimating(false);
                      setSelectedDateOffset(selectedDateOffset - 1);
                      setFetchingHistoricalData(true);
                    }}
                    disabled={fetchingHistoricalData || selectedDateOffset <= oldestAvailableOffset || isAnimating}
                    compact
                    contentStyle={styles.dateNavButtonContent}
                    labelStyle={styles.dateNavButtonLabel}
                  >
                    {' '}
                  </Button>
                  <View style={styles.dateDisplay}>
                    <Paragraph style={styles.dateText}>
                      {selectedTimeStep
                        ? new Date(selectedTimeStep).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                        : 'Loading…'}
                    </Paragraph>
                    {isAnimating && (
                      <Paragraph style={styles.animationIndicator}>
                        Day {Math.abs(selectedDateOffset) + 1} of 8
                      </Paragraph>
                    )}
                  </View>
                  <Button
                    mode="text"
                    icon="chevron-right"
                    onPress={() => {
                      setIsAnimating(false);
                      setSelectedDateOffset(selectedDateOffset + 1);
                      setFetchingHistoricalData(true);
                    }}
                    disabled={fetchingHistoricalData || selectedDateOffset >= 0 || isAnimating}
                    compact
                    contentStyle={styles.dateNavButtonContent}
                    labelStyle={styles.dateNavButtonLabel}
                  >
                    {' '}
                  </Button>
                </View>
              )}
            </View>
          )}
          {selectedOverlay === 'weedlines' && (
            <View style={styles.chlorophyllDatasetContainer}>
              <Title style={styles.datasetLabel}>Dataset:</Title>
              <Menu
                visible={showWeedlinesMenu}
                onDismiss={() => setShowWeedlinesMenu(false)}
                anchor={
                  <Button
                    mode="outlined"
                    onPress={() => setShowWeedlinesMenu(true)}
                    icon="chevron-down"
                    contentStyle={styles.datasetButton}
                    style={styles.datasetButtonContainer}
                    compact
                  >
                    {weedlinesDatasets.find(d => d.id === weedlinesDataset)?.label || 'Select Dataset'}
                  </Button>
                }
              >
                {weedlinesDatasets.map((dataset) => (
                  <Menu.Item
                    key={dataset.id}
                    onPress={() => {
                      setWeedlinesDataset(dataset.id as '1day' | '3day');
                      setShowWeedlinesMenu(false);
                      // Reset history when switching datasets.
                      setSelectedDateOffset(0);
                      setIsAnimating(false);
                    }}
                    title={dataset.label}
                    leadingIcon={weedlinesDataset === dataset.id ? 'check' : undefined}
                  />
                ))}
              </Menu>
              {/* Date Navigation Controls for Weedlines */}
              {(
                <View style={styles.dateNavigationContainer}>
                  <Button
                    mode="text"
                    icon={isAnimating ? "pause" : "play"}
                    onPress={() => {
                      setIsAnimating(!isAnimating);
                      if (!isAnimating) {
                        setSelectedDateOffset(-Math.min(7, availableTimeSteps.length - 1));
                      }
                    }}
                    disabled={fetchingHistoricalData || availableTimeSteps.length < 2}
                    compact
                    contentStyle={styles.dateNavButtonContent}
                    labelStyle={styles.dateNavButtonLabel}
                  >
                    {' '}
                  </Button>
                  <Button
                    mode="text"
                    icon="chevron-left"
                    onPress={() => {
                      setIsAnimating(false);
                      setSelectedDateOffset(selectedDateOffset - 1);
                      setFetchingHistoricalData(true);
                    }}
                    disabled={fetchingHistoricalData || selectedDateOffset <= oldestAvailableOffset || isAnimating}
                    compact
                    contentStyle={styles.dateNavButtonContent}
                    labelStyle={styles.dateNavButtonLabel}
                  >
                    {' '}
                  </Button>
                  <View style={styles.dateDisplay}>
                    <Paragraph style={styles.dateText}>
                      {selectedTimeStep
                        ? new Date(selectedTimeStep).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                        : 'Loading…'}
                    </Paragraph>
                    {isAnimating && (
                      <Paragraph style={styles.animationIndicator}>
                        Day {Math.abs(selectedDateOffset) + 1} of 8
                      </Paragraph>
                    )}
                  </View>
                  <Button
                    mode="text"
                    icon="chevron-right"
                    onPress={() => {
                      setIsAnimating(false);
                      setSelectedDateOffset(selectedDateOffset + 1);
                      setFetchingHistoricalData(true);
                    }}
                    disabled={fetchingHistoricalData || selectedDateOffset >= 0 || isAnimating}
                    compact
                    contentStyle={styles.dateNavButtonContent}
                    labelStyle={styles.dateNavButtonLabel}
                  >
                    {' '}
                  </Button>
                </View>
              )}
            </View>
          )}
          {showCurrentsOverlay && (
            <View style={styles.chlorophyllDatasetContainer}>
              <Title style={styles.datasetLabel}>Currents:</Title>
              <Menu
                visible={showCurrentsMenu}
                onDismiss={() => setShowCurrentsMenu(false)}
                anchor={
                  <Button
                    mode="outlined"
                    onPress={() => setShowCurrentsMenu(true)}
                    icon="chevron-down"
                    contentStyle={styles.datasetButton}
                    style={styles.datasetButtonContainer}
                    compact
                  >
                    {currentsDatasets.find(d => d.id === currentsDataset)?.label || 'Select Dataset'}
                  </Button>
                }
              >
                {currentsDatasets.map((dataset) => (
                  <Menu.Item
                    key={dataset.id}
                    onPress={() => {
                      setCurrentsDataset(dataset.id as 'default' | 'miami');
                      setShowCurrentsMenu(false);
                    }}
                    title={dataset.label}
                    titleStyle={{ fontSize: 14 }}
                  />
                ))}
              </Menu>
              {/* Date Navigation Controls for Currents */}
              <View style={styles.dateNavigationContainer}>
                <Button
                  mode="text"
                  icon="chevron-left"
                  onPress={() => {
                    setSelectedDateOffset(selectedDateOffset - 1);
                    setFetchingHistoricalData(true);
                  }}
                  disabled={fetchingHistoricalData || selectedDateOffset <= oldestAvailableOffset}
                  compact
                  contentStyle={styles.dateNavButtonContent}
                  labelStyle={styles.dateNavButtonLabel}
                >
                  {' '}
                </Button>
                <View style={styles.dateDisplay}>
                  <Paragraph style={styles.dateText}>
                    {selectedTimeStep
                      ? new Date(selectedTimeStep).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      : 'Loading…'}
                  </Paragraph>
                </View>
                <Button
                  mode="text"
                  icon="chevron-right"
                  onPress={() => {
                    setSelectedDateOffset(selectedDateOffset + 1);
                    setFetchingHistoricalData(true);
                  }}
                  disabled={fetchingHistoricalData || selectedDateOffset >= 0}
                  compact
                  contentStyle={styles.dateNavButtonContent}
                  labelStyle={styles.dateNavButtonLabel}
                >
                  {' '}
                </Button>
              </View>
            </View>
          )}
          {locationError && (
            <Title style={styles.errorText}>{locationError}</Title>
          )}
          {(selectedOverlay || showCurrentsOverlay || showRadarOverlay) && (
            <View style={styles.opacityContainer}>
              <Paragraph style={styles.opacityLabel}>Opacity</Paragraph>
              <Slider
                style={styles.opacitySlider}
                minimumValue={0.1}
                maximumValue={1}
                value={overlayOpacity}
                onValueChange={setOverlayOpacity}
                minimumTrackTintColor={theme.colors.primary}
                maximumTrackTintColor="#E0E0E0"
                thumbTintColor={theme.colors.primary}
              />
              <Paragraph style={styles.opacityValue}>{Math.round(overlayOpacity * 100)}%</Paragraph>
            </View>
          )}
          {datasetTimestamp && (selectedOverlay || showCurrentsOverlay) && (
            <View style={styles.timestampContainer}>
              <Paragraph style={styles.timestampText}>
                Dataset: {formatDatasetTimestamp(datasetTimestamp)}
              </Paragraph>
              {fetchingHistoricalData && (
                <ActivityIndicator 
                  size="small" 
                  color={theme.colors.primary} 
                  style={styles.historyLoadingIndicator} 
                />
              )}
            </View>
          )}
        </Card.Content>
      </Card>
      )}

      {/* Compact Bottom Controls */}
      <View style={[
        styles.bottomControlsContainer, 
        { 
          bottom: Dimensions.get('window').height < 500 ? insets.bottom + 20 : insets.bottom + 50, 
          right: insets.right + 8,
        }
      ]}>
        {/* Expand/Collapse Toggle */}
        <FAB
          style={[styles.toggleFab, { backgroundColor: theme.colors.primary }]}
          icon={isControlsExpanded ? "chevron-down" : "chevron-up"}
          small
          onPress={() => setIsControlsExpanded(!isControlsExpanded)}
          color="white"
          accessibilityLabel={isControlsExpanded ? "Collapse controls" : "Expand controls"}
        />

        {/* GPS Location Button */}
        <FAB
          style={[styles.gpsFab, { backgroundColor: theme.colors.surface }]}
          icon="crosshairs-gps"
          small
          onPress={centerOnUserLocation}
          color={theme.colors.primary}
          accessibilityLabel="Center on my location"
        />
        
        {/* Clear AI Waypoint Button - Show when there's a selected location or report hotspots */}
        {(selectedLocation && !navigateToLocation) || (reportHotspots && reportHotspots.length > 0) ? (
          <FAB
            style={[styles.clearWaypointFab, { backgroundColor: theme.colors.errorContainer }]}
            icon="map-marker-off"
            small
            onPress={() => {
              dispatch(clearMapLocation());
              setReportHotspots(null);
              setEnableClustering(false);
            }}
            color={theme.colors.error}
            accessibilityLabel="Clear fishing waypoints"
          />
        ) : null}

        {/* Show controls only when expanded */}
        {isControlsExpanded && (
          <>
            {/* Measurement Tool Toggle */}
            <FAB
              style={[
                styles.measurementFab,
                { 
                  backgroundColor: measurementMode ? theme.colors.primary : theme.colors.surface,
                }
              ]}
              icon="ruler"
              small
              onPress={() => {
                setMeasurementMode(!measurementMode);
                if (!measurementMode) {
                  // Entering measurement mode
                  toast.info('Tap on the map or any marker to measure distance');
                } else {
                  // Exiting measurement mode
                  clearMeasurement();
                }
              }}
              color={measurementMode ? 'white' : theme.colors.primary}
              accessibilityLabel="Toggle measurement tool"
            />

            {/* Clear Measurement FAB - Only show when in measurement mode */}
            {measurementMode && measurementPoints.length > 0 && (
              <FAB
                style={[styles.clearMeasurementFab, { backgroundColor: theme.colors.error }]}
                icon="close"
                small
                onPress={clearMeasurement}
                color="white"
                accessibilityLabel="Clear measurement"
              />
            )}

            {/* Fishing Spots Toggle - Compact */}
            <FAB
              style={[
                styles.fishingSpotsFab,
                { 
                  backgroundColor: showFishingSpots ? theme.colors.primary : theme.colors.surface,
                }
              ]}
              icon={loadingPublicWaypoints ? "loading" : "map-marker"}
              small
              onPress={() => {
                if (!loadingPublicWaypoints) {
                  if (showFishingSpots) {
                    // If waypoints are showing, turn them off
                    setShowFishingSpots(false);
                    setSelectedWaypointCategory(null);
                  } else {
                    // Show category selection modal
                    setShowWaypointCategoryModal(true);
                  }
                }
              }}
              color={showFishingSpots ? 'white' : theme.colors.primary}
              accessibilityLabel="Toggle waypoints"
              disabled={loadingPublicWaypoints}
            />


            {/* Relief Shading Toggle */}
            <FAB
              style={[
                styles.bathymetryFab,
                { 
                  backgroundColor: showBathymetryOverlay ? theme.colors.primary : theme.colors.surface,
                }
              ]}
              icon="terrain"
              small
              onPress={() => {
                if (!showBathymetryOverlay) {
                  // Show dialog to select bathymetry type
                  setBathymetryDialogVisible(true);
                } else {
                  // Turn off bathymetry
                  setShowBathymetryOverlay(false);
                }
              }}
              onLongPress={() => {
                // Long press to switch between types when already enabled
                if (showBathymetryOverlay) {
                  setBathymetryDialogVisible(true);
                }
              }}
              color={showBathymetryOverlay ? 'white' : theme.colors.primary}
              accessibilityLabel="Toggle relief shading"
            />

            {/* Inspection Tool Toggle */}
            <FAB
              style={[
                styles.inspectionFab,
                { 
                  backgroundColor: inspectionMode ? theme.colors.primary : theme.colors.surface,
                }
              ]}
              icon="crosshairs-question"
              small
              onPress={() => {
                setInspectionMode(!inspectionMode);
                if (inspectionMode) {
                  setInspectedCoordinate(null);
                  setInspectedDataValue(null);
                  setInspectedDepthValue(null);
                }
                // Disable other modes when enabling inspection
                if (!inspectionMode) {
                  setMeasurementMode(false);
                  clearMeasurement();
                }
              }}
              color={inspectionMode ? 'white' : theme.colors.primary}
              accessibilityLabel="Toggle coordinate inspection"
            />
          </>
        )}
        
      </View>

      
      
      {/* Inspection Mode Crosshair Overlay */}
      {inspectionMode && !inspectedCoordinate && (
        <View style={[styles.crosshairContainer, { zIndex: showOceanDataPanel ? 4 : 5 }]} pointerEvents="none">
          <View style={styles.crosshairHorizontal} />
          <View style={styles.crosshairVertical} />
          <View style={styles.crosshairCenter} />
        </View>
      )}
      
      {/* Inspection Coordinate Display */}
      {inspectionMode && inspectedCoordinate && (
        <Card style={[styles.inspectionCard, { 
          backgroundColor: theme.colors.surface,
          zIndex: showOceanDataPanel ? 8 : 10  // Lower z-index when ocean panel is open, but still above hints
        }]}>
          <Card.Content style={styles.inspectionContent}>
            <View style={styles.inspectionHeader}>
              <MaterialIcons name="location-on" size={24} color={theme.colors.primary} />
              <Title style={styles.inspectionTitle}>Center Coordinates</Title>
              <IconButton
                icon="close"
                size={20}
                onPress={() => {
                  setInspectedCoordinate(null);
                  setInspectedDataValue(null);
                  setInspectedDepthValue(null);
                }}
                style={styles.inspectionCloseButton}
              />
            </View>
            <View style={styles.coordinateInfo}>
              <View style={styles.coordinateRow}>
                <Paragraph style={styles.coordinateLabel}>Latitude:</Paragraph>
                <Paragraph style={styles.coordinateValue}>{inspectedCoordinate.latitude.toFixed(6)}°</Paragraph>
              </View>
              <View style={styles.coordinateRow}>
                <Paragraph style={styles.coordinateLabel}>Longitude:</Paragraph>
                <Paragraph style={styles.coordinateValue}>{inspectedCoordinate.longitude.toFixed(6)}°</Paragraph>
              </View>
              <View style={styles.coordinateDMSRow}>
                <Paragraph style={styles.coordinateLabel}>DMS:</Paragraph>
                <View style={styles.dmsContainer}>
                  <Paragraph style={styles.coordinateDMS}>
                    {formatCoordinateDMS(inspectedCoordinate.latitude, inspectedCoordinate.longitude)}
                  </Paragraph>
                </View>
              </View>
            </View>
            
            {/* Depth Display */}
            <View style={styles.oceanDataSection}>
              <View style={styles.oceanDataDivider} />
              {fetchingDepthValue ? (
                <View style={styles.oceanDataLoading}>
                  <ActivityIndicator size="small" color={theme.colors.primary} />
                  <Paragraph style={styles.oceanDataLoadingText}>Fetching depth...</Paragraph>
                </View>
              ) : inspectedDepthValue ? (
                <View style={styles.oceanDataContent}>
                  <View style={styles.oceanDataRow}>
                    <Paragraph style={styles.oceanDataLabel}>Depth:</Paragraph>
                    <Paragraph style={styles.oceanDataValue}>
                      {inspectedDepthValue.formattedValue}
                    </Paragraph>
                  </View>
                </View>
              ) : (
                <Paragraph style={styles.oceanDataNoData}>
                  Depth data unavailable
                </Paragraph>
              )}
            </View>
            
            {/* Ocean Data Display */}
            {(selectedOverlay || showCurrentsOverlay) && (
              <View style={styles.oceanDataSection}>
                <View style={styles.oceanDataDivider} />
                {fetchingDataValue ? (
                  <View style={styles.oceanDataLoading}>
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                    <Paragraph style={styles.oceanDataLoadingText}>Fetching ocean data...</Paragraph>
                  </View>
                ) : inspectedDataValue ? (
                  <View style={styles.oceanDataContent}>
                    <View style={styles.oceanDataRow}>
                      <Paragraph style={styles.oceanDataLabel}>
                        {showCurrentsOverlay ? 'Current:' : 
                         selectedOverlay === 'sst' ? 'Temperature:' :
                         selectedOverlay === 'chlorophyll' ? 'Chlorophyll:' :
                         selectedOverlay === 'ssh' ? 'Sea Height:' :
                         selectedOverlay === 'weedlines' ? 'Weedlines:' : 'Value:'}
                      </Paragraph>
                      <Paragraph style={styles.oceanDataValue}>
                        {inspectedDataValue.formattedValue}
                      </Paragraph>
                    </View>
                    {datasetTimestamp && (
                      <Paragraph style={styles.oceanDataTimestamp}>
                        Data from: {formatDatasetTimestamp(datasetTimestamp)}
                      </Paragraph>
                    )}
                  </View>
                ) : (
                  <Paragraph style={styles.oceanDataNoData}>
                    No data available at this location
                  </Paragraph>
                )}
              </View>
            )}
            
            <View style={styles.inspectionButtons}>
              <Button
                mode="contained"
                icon="map-marker-plus"
                onPress={() => {
                  setIsCreateModalVisible(true);
                  setNewWaypointCoordinates(inspectedCoordinate);
                  setInspectedCoordinate(null);
                }}
                style={[styles.inspectionButton, { marginRight: 8 }]}
                compact
              >
                Save Waypoint
              </Button>
              <Button
                mode="contained"
                icon="share"
                onPress={async () => {
                  const message = `Location: ${inspectedCoordinate.latitude.toFixed(6)}, ${inspectedCoordinate.longitude.toFixed(6)}\nDMS: ${formatCoordinateDMS(inspectedCoordinate.latitude, inspectedCoordinate.longitude)}\n\nShared from Ocean Insight Pro`;
                  
                  try {
                    await Share.share({
                      message,
                      title: 'Ocean Insight Pro Coordinates',
                    });
                  } catch (error) {
                    logger.error('Error sharing coordinates', error instanceof Error ? error : new Error(String(error)));
                  }
                }}
                style={[styles.inspectionButton, { flex: 1 }]}
                compact
              >
                Share
              </Button>
            </View>
          </Card.Content>
        </Card>
      )}

      {/* Waypoint Creation Modal */}
      {isCreateModalVisible && (
        <WaypointEditModal
          waypoint={null}
          visible={isCreateModalVisible}
          initialCoordinates={newWaypointCoordinates || undefined}
          onClose={() => {
            setIsCreateModalVisible(false);
            setNewWaypointCoordinates(null);
          }}
          onSave={async (waypoint) => {
            // Reload waypoints in current viewport after saving
            await loadPrivateWaypointsInViewport();
            setIsCreateModalVisible(false);
            setNewWaypointCoordinates(null);
            // Show success notification
            toast.success(`Waypoint "${waypoint.name}" saved successfully`);
          }}
        />
      )}

      {/* Waypoint Category Selection Modal */}
      <WaypointCategoryModal
        visible={showWaypointCategoryModal}
        onDismiss={() => setShowWaypointCategoryModal(false)}
        currentCategory={selectedWaypointCategory}
        showPrivateWaypoints={showPrivateWaypoints}
        onTogglePrivateWaypoints={() => setShowPrivateWaypoints(!showPrivateWaypoints)}
        onSelectCategory={(category) => {
          setSelectedWaypointCategory(category);
          setShowFishingSpots(true);
          setShowWaypointCategoryModal(false);
        }}
      />
      
      {/* Relief Shading Selection Modal */}
      <Portal>
        <Modal 
          visible={bathymetryDialogVisible} 
          onDismiss={() => setBathymetryDialogVisible(false)}
          contentContainerStyle={{ 
            margin: 20,
            backgroundColor: theme.colors.surface,
            borderRadius: 12
          }}
        >
          <Card>
            <Card.Content>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Title style={{ fontSize: 20, fontWeight: 'bold' }}>Relief Shading</Title>
                <IconButton
                  icon="close"
                  size={24}
                  onPress={() => setBathymetryDialogVisible(false)}
                  style={{ margin: -8 }}
                />
              </View>
              
              <Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 16, fontSize: 14 }}>
                Choose depth visualization for the ocean floor
              </Text>
              
              <Divider style={{ marginBottom: 8 }} />
              
              <View>
                {/* High-Resolution Surveys */}
                <List.Item
                  title="High-Resolution Surveys"
                  description="Detailed bathymetric survey data"
                  left={props => (
                    <List.Icon 
                      {...props} 
                      icon="terrain"
                      color={showBathymetryOverlay && bathymetryType === 'noaa_bag' ? theme.colors.primary : undefined}
                    />
                  )}
                  right={props => 
                    showBathymetryOverlay && bathymetryType === 'noaa_bag' ? (
                      <List.Icon {...props} icon="check" color={theme.colors.primary} />
                    ) : null
                  }
                  onPress={() => {
                    setBathymetryType('noaa_bag');
                    setShowBathymetryOverlay(true);
                    setBathymetryDialogVisible(false);
                  }}
                  style={[
                    { paddingVertical: 8, borderRadius: 8 },
                    showBathymetryOverlay && bathymetryType === 'noaa_bag' && { backgroundColor: theme.colors.primaryContainer }
                  ]}
                  titleStyle={showBathymetryOverlay && bathymetryType === 'noaa_bag' ? { color: theme.colors.primary, fontWeight: '600' } : undefined}
                />
                
                {/* Multibeam Coverage */}
                <List.Item
                  title="Multibeam Coverage"
                  description="Wide-area bathymetric mosaic"
                  left={props => (
                    <List.Icon 
                      {...props} 
                      icon="map-legend"
                      color={showBathymetryOverlay && bathymetryType === 'multibeam' ? theme.colors.primary : undefined}
                    />
                  )}
                  right={props => 
                    showBathymetryOverlay && bathymetryType === 'multibeam' ? (
                      <List.Icon {...props} icon="check" color={theme.colors.primary} />
                    ) : null
                  }
                  onPress={() => {
                    setBathymetryType('multibeam');
                    setShowBathymetryOverlay(true);
                    setBathymetryDialogVisible(false);
                  }}
                  style={[
                    { paddingVertical: 8, borderRadius: 8 },
                    showBathymetryOverlay && bathymetryType === 'multibeam' && { backgroundColor: theme.colors.primaryContainer }
                  ]}
                  titleStyle={showBathymetryOverlay && bathymetryType === 'multibeam' ? { color: theme.colors.primary, fontWeight: '600' } : undefined}
                />
              </View>
            </Card.Content>
          </Card>
        </Modal>
      </Portal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  map: {
    flex: 1,
  },
  menuToggleFab: {
    position: 'absolute',
    elevation: 4,
    zIndex: 11,
  },
  overlayCard: {
    position: 'absolute',
    elevation: 4,
    borderRadius: 12,
    maxWidth: 500,
    zIndex: 10,
  },
  compactCardContent: {
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  overlayButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4,
  },
  overlayChip: {
    marginBottom: 2,
    height: 32,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  loadingText: {
    fontSize: 14,
    marginLeft: 8,
  },
  zoomMessage: {
    fontSize: 12,
    color: '#666',
    marginTop: 8,
    fontStyle: 'italic',
  },
  bottomControlsContainer: {
    position: 'absolute',
    right: 16,
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    zIndex: 10,
  },
  fishingSpotsFab: {
    elevation: 4,
  },
  bathymetryFab: {
    elevation: 4,
  },
  inspectionFab: {
    elevation: 4,
  },
  errorText: {
    fontSize: 12,
    color: '#FF5252',
    marginTop: 4,
    fontStyle: 'italic',
  },
  chlorophyllDatasetContainer: {
    marginTop: 12,
    alignItems: 'center',
    width: '100%',
  },
  datasetButtonContainer: {
    borderRadius: 20,
    minWidth: 200,
  },
  datasetButton: {
    flexDirection: 'row-reverse',
  },
  datasetLabel: {
    fontSize: 12,
    marginBottom: 4,
    opacity: 0.7,
  },
  measurementFab: {
    elevation: 4,
  },
  clearMeasurementFab: {
    elevation: 4,
  },
  measurementMarker: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 3,
    borderColor: 'white',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.5,
    shadowRadius: 3,
    elevation: 5,
  },
  distanceLabel: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    elevation: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.1)',
  },
  distanceText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  zoomControlsContainer: {
    position: 'absolute',
    bottom: 100,
    left: 8,
    flexDirection: 'column',
    gap: 8,
    zIndex: 10,
  },
  zoomButton: {
    elevation: 4,
  },
  toggleFab: {
    elevation: 4,
  },
  gpsFab: {
    elevation: 4,
    marginTop: 8,
  },
  clearWaypointFab: {
    elevation: 4,
    marginTop: 8,
  },
  timestampContainer: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
    alignItems: 'center',
  },
  timestampText: {
    fontSize: 12,
    color: '#666',
    fontStyle: 'italic',
  },
  dateNavigationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    backgroundColor: '#f5f5f5',
    borderRadius: 20,
    paddingVertical: 4,
    paddingHorizontal: 8,
    alignSelf: 'center',
  },
  dateNavButtonContent: {
    flexDirection: 'row-reverse',
    minWidth: 40,
  },
  dateNavButtonLabel: {
    marginHorizontal: 0,
  },
  dateDisplay: {
    minWidth: 120,
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  dateText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
  animationIndicator: {
    fontSize: 11,
    color: '#666',
    fontStyle: 'italic',
    marginTop: 2,
  },
  historyLoadingIndicator: {
    marginLeft: 8,
  },
  inspectionCard: {
    position: 'absolute',
    top: 120,
    left: 16,
    right: 16,
    elevation: 6,
    borderRadius: 16,
    maxWidth: 420,
    alignSelf: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  inspectionContent: {
    paddingVertical: 16,
    paddingHorizontal: 20,
  },
  inspectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  inspectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginLeft: 8,
    flex: 1,
  },
  inspectionCloseButton: {
    margin: -8,
  },
  coordinateInfo: {
    marginBottom: 16,
  },
  coordinateRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    minHeight: 32,
  },
  coordinateDMSRow: {
    paddingVertical: 6,
    marginTop: 4,
  },
  coordinateLabel: {
    fontSize: 14,
    opacity: 0.6,
    minWidth: 80,
  },
  coordinateValue: {
    fontSize: 15,
    fontWeight: '600',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    textAlign: 'right',
    flex: 1,
    marginLeft: 8,
  },
  dmsContainer: {
    marginTop: 4,
  },
  coordinateDMS: {
    fontSize: 14,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    lineHeight: 20,
    opacity: 0.9,
  },
  shareButton: {
    marginTop: 4,
    borderRadius: 8,
  },
  inspectionButtons: {
    flexDirection: 'row',
    marginTop: 12,
    justifyContent: 'space-between',
  },
  inspectionButton: {
    borderRadius: 8,
    flex: 1,
  },
  crosshairContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  crosshairHorizontal: {
    position: 'absolute',
    width: 40,
    height: 2,
    backgroundColor: '#000000',
    opacity: 0.8,
  },
  crosshairVertical: {
    position: 'absolute',
    width: 2,
    height: 40,
    backgroundColor: '#000000',
    opacity: 0.8,
  },
  crosshairCenter: {
    position: 'absolute',
    width: 10,
    height: 10,
    borderRadius: 5,
    borderWidth: 2,
    borderColor: '#000000',
    backgroundColor: 'transparent',
  },
  expandedControlsScroll: {
    maxHeight: 200,
  },
  oceanDataSection: {
    marginTop: 12,
  },
  oceanDataDivider: {
    height: 1,
    backgroundColor: '#E0E0E0',
    marginBottom: 12,
  },
  oceanDataLoading: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
  },
  oceanDataLoadingText: {
    marginLeft: 8,
    fontSize: 14,
    opacity: 0.6,
  },
  oceanDataContent: {
    paddingVertical: 4,
  },
  oceanDataRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  oceanDataLabel: {
    fontSize: 14,
    opacity: 0.6,
    minWidth: 100,
  },
  oceanDataValue: {
    fontSize: 15,
    fontWeight: '600',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    textAlign: 'right',
    flex: 1,
    marginLeft: 8,
  },
  oceanDataTimestamp: {
    fontSize: 12,
    opacity: 0.5,
    fontStyle: 'italic',
    marginTop: 4,
  },
  oceanDataNoData: {
    fontSize: 14,
    opacity: 0.5,
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 8,
  },
  opacityContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    paddingHorizontal: 8,
    backgroundColor: '#f5f5f5',
    borderRadius: 20,
    paddingVertical: 8,
  },
  opacityLabel: {
    fontSize: 13,
    color: '#666',
    marginRight: 8,
    minWidth: 50,
  },
  opacitySlider: {
    flex: 1,
    height: 30,
  },
  opacityValue: {
    fontSize: 13,
    color: '#333',
    fontWeight: '500',
    marginLeft: 8,
    minWidth: 40,
    textAlign: 'right',
  },
});
